import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { useShiftAssetId } from "@/shared/hooks/useShiftAssetId";
import { routeDelaysManager } from "@/setup/routes/delay.routes";
import { useUserScope } from "@/setup/scope";
import { ATHENS_GRAY } from "@/shared/colors";
import { useDelayAssetsGrouped } from "@/shared/selectors/AssetButtonGroup";
import {
	ErrorModal,
	HelmetDexteel,
	ShiftDayNavigatorControl,
	useShiftNavigator,
} from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { get } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LinesFilter } from "../shared/components/filters/LinesFilter";
import { DelaysManagerTable } from "./components/table/DelaysManagerTable";
import { DelayByShift } from "./models/DelayByShift";
import {
	getDelay,
	getDelaysByShift,
} from "./repositories/DelaysManagerRepository";

type useDelaysByShiftProps = {
	shiftId: number | undefined;
	delayAreaAssetId: number | null;
	onlyUndeclared?: boolean;
	onError: (error: Error) => void;
};

export const useDelaysByShift = ({
	shiftId,
	delayAreaAssetId,
	onlyUndeclared = false,
	onError,
}: useDelaysByShiftProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: ["delays", shiftId, delayAreaAssetId, onlyUndeclared],
		queryFn: () =>
			getDelaysByShift({
				shiftId: shiftId || null,
				delayAreaAssetId: delayAreaAssetId,
				onlyUndeclared: onlyUndeclared,
			}),
		onError: onError,
	});
};

type useDelayByIdProps = {
	delayId: number | null;
	onError: (error: Error) => void;
};

export const useDelayById = ({ delayId, onError }: useDelayByIdProps) => {
	return useQuery<DelayByShift[], Error>({
		queryKey: ["delays", delayId],
		queryFn: () =>
			getDelay({
				delayId,
			}),
		onError: onError,
	});
};

type Props = {};

export const DelaysManagerPage = (props: Props) => {
	const [error, setError] = useState("");
	const [notificationMessage, setNotificationMessage] = useState("");

	const navigate = useNavigate();
	const { shiftId: shiftIdParam } = useParams();
	const [params] = useSearchParams();
	const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();
	const [filterLine, setFilterLine] = useState<number | null>(null);
	const scope = useUserScope();
	// "All" = the user's whole scope
	const scopeAssetId = filterLine ?? scope.defaultAsset?.AssetId ?? null;
	const delayId = params.get("delayId");
	const action = params.get("action") as "edit" | "justify" | "split" | null;
	const onlyUndeclared = params.get("onlyUndeclared") === "true";

	// Navigator shift scheme: own, else area, else Plant
	const { data: groupedAssets, isLoading: isGroupedLoading } =
		useDelayAssetsGrouped();
	const parentAssetId = useMemo(() => {
		if (!scopeAssetId || !groupedAssets) return null;
		return (
			groupedAssets.find((a) => a.AssetId === scopeAssetId)?.ParentAssetId ??
			null
		);
	}, [scopeAssetId, groupedAssets]);
	const schemeOwnerId = useShiftAssetId(scopeAssetId, parentAssetId);
	// Null while the parent is unknown
	const shiftAssetId = scopeAssetId && isGroupedLoading ? null : schemeOwnerId;

	const updateUrl = (
		newOnlyUndeclared?: boolean,
		newAction?: string | null,
		newDelayId?: string | null,
	) => {
		const shiftId = get(shiftInfo, "CurrentShiftId", null);
		const urlParams = new URLSearchParams();
		const targetDelayId = newDelayId !== undefined ? newDelayId : delayId;
		if (targetDelayId) urlParams.append("delayId", targetDelayId);
		const targetAction = newAction !== undefined ? newAction : action;
		if (targetAction) urlParams.append("action", targetAction);
		if (newOnlyUndeclared ?? onlyUndeclared)
			urlParams.append("onlyUndeclared", "true");

		navigate(`${routeDelaysManager}/${shiftId}?${urlParams.toString()}`, {
			replace: true,
		});
	};

	const {
		data: delays,
		refetch,
		isLoading,
	} = useDelaysByShift({
		shiftId: shiftInfo?.CurrentShiftId,
		delayAreaAssetId: scopeAssetId,
		onlyUndeclared: onlyUndeclared,
		onError: (e) => setError(e.message),
	});

	useEffect(() => {
		if (shiftInfo) {
			updateUrl();
			(async () => {
				await refetch();
			})();
		}
	}, [shiftInfo, scopeAssetId, onlyUndeclared]);

	// URL shiftId at mount: updateUrl can overwrite it before the first load
	const initialShiftId = useRef(shiftIdParam);
	const loadedShiftAssetId = useRef<number | null>(null);
	// Scheme switch drops the shiftId: MES.GetShiftByParameters ignores @AssetId
	// when @ShiftId is set
	// biome-ignore lint/correctness/useExhaustiveDependencies: scheme owner only
	useEffect(() => {
		if (shiftAssetId == null || shiftAssetId === loadedShiftAssetId.current)
			return;
		const isInit = loadedShiftAssetId.current == null;
		loadedShiftAssetId.current = shiftAssetId;
		(async () => {
			if (isInit) {
				await getShiftDataFromAPI(
					null,
					Number(initialShiftId.current) || 0,
					shiftAssetId,
				);
			} else {
				await getShiftDataFromAPI(
					shiftInfo?.CurrentProductionDate ?? null,
					0,
					shiftAssetId,
				);
			}
		})();
	}, [shiftAssetId]);

	return (
		<>
			<HelmetDexteel title="Delays Manager" />
			<Grid2
				container
				size={{ md: 12, xs: 12 }}
				spacing={1}
				px={2}
				pt={2}
				style={{ backgroundColor: ATHENS_GRAY }}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" style={{ fontWeight: 600 }}>
						Delays Manager
					</Typography>
				</Grid2>

				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<ShiftDayNavigatorControl
							preventInit
							withPaper={false}
							assetId={shiftAssetId ?? 1}
						/>
					</Paper>
				</Grid2>
				<Grid2 size={{ xs: "auto" }}>
					<Paper
						elevation={1}
						sx={{
							padding: 2,
							height: "100%",
							display: "flex",
							alignItems: "center",
						}}
					>
						<LinesFilter value={filterLine} onChange={setFilterLine} />
					</Paper>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<DelaysManagerTable
						delayId={delayId}
						action={action}
						isLoading={isLoading}
						rows={delays || []}
						refreshData={refetch}
						assetId={filterLine ?? undefined}
						updateUrl={updateUrl}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<NotificationSnackBar
				message={notificationMessage}
				onHide={() => setNotificationMessage("")}
			/>
		</>
	);
};
